import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import AlertMessage from "../../../../components/AlertMessage";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { SelectItemDto } from "../../../../types/SelectItemDto";
import { createTopic, deleteTopic, downloadTopicsTemplate, exportTopicsToCsv, getTopicDetailById, getTopicTree, updateTopic } from "../api/topicApi";
import { TopicDetailDto, TopicDto } from "../types";
import TopicDetailForm from "../components/TopicDetailForm";
import TopicsTreePanel from "../components/TopicsTreePanel";
import { getTopicFormSchema } from "../schema/topicSchema";
import "./TopicsManagement.css";
import { getTranslatedDescription } from "../../../../utils/formatters/UserLanguageToDescriptionField";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../../hooks/useModal";
import { useUserPermissions } from "../../../../utils/auth";
import { PERMISSIONS } from "../../../../constants/permissions";
import TopicImportFromCSV from "../components/TopicImportFromCSV";

const getAllNodes = (nodes: TopicDto[]): TopicDto[] =>
    nodes.flatMap((node) => [node, ...getAllNodes(node.childrens || [])]);

const getFirstNodeId = (nodes: TopicDto[]): number | null => {
    if (!nodes.length) return null;
    return nodes[0].id;
};

export default function TopicsManagement() {
    const { t, i18n } = useTranslation(["topic", "common"]);
    const queryClient = useQueryClient();
    const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
    const [previousSelectedTopicId, setPreviousSelectedTopicId] = useState<number | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string | undefined }>({});
    const [editableDetail, setEditableDetail] = useState<TopicDetailDto | null>(null);
    const { showModal: showDeleteModal, modalData: deleteModalData, openModal: openDeleteModal, closeModal: closeDeleteModal, isLoading: isDeleteLoading, setLoading: setDeleteLoading } = useModal<TopicDto>();
    const { showModal: showCannotDeleteModal, modalData: cannotDeleteModalData, openModal: openCannotDeleteModal, closeModal: closeCannotDeleteModal } = useModal<TopicDto>();
    const { showModal: showExportModal, modalData: exportModalData, openModal: openExportModal, closeModal: closeExportModal, isLoading: isExportLoading, setLoading: setExportLoading } = useModal<TopicDto>();
    const permissions = useUserPermissions();
    const [searchText, setSearchText] = useState<string>("");
    const [showImportFromCsvPage, setShowImportFromCsvPage] = useState<boolean>(false);
    
    const {
        data: topicTree = [],
        isLoading: isLoadingTree,
        error: treeError,
    } = useQuery({
        queryKey: ["adminTopicsTree"],
        queryFn: getTopicTree,
    });

    const {
        data: topicDetail,
        isLoading: isLoadingDetail,
        error: detailError,
    } = useQuery<TopicDetailDto>({
        queryKey: ["adminTopicDetail", selectedTopicId],
        queryFn: () => getTopicDetailById(selectedTopicId!),
        enabled: selectedTopicId != null,
    });

    useEffect(() => {
        if (!topicTree.length) return;
        if (selectedTopicId == null) {
            setSelectedTopicId(getFirstNodeId(topicTree));
        }
    }, [topicTree]);

    const topicOptions: SelectItemDto[] = useMemo(
        () =>
            getAllNodes(topicTree).map((node) => ({
                id: node.id,
                name: `${node.code ? `${node.code} - ` : ""}${getTranslatedDescription(node.name, i18n.language)}`,
            })),
        [topicTree, i18n.language]
    );

    const normalizeTopicDetail = (detail: TopicDetailDto): TopicDetailDto => ({
        ...detail,
        code: detail.code || "",
        description: {
            catalan: detail.description?.catalan || "",
            spanish: detail.description?.spanish || "",
            english: detail.description?.english || "",
        },
    });

    useEffect(() => {
        if (!topicDetail) return;
        if (!isCreating) {
            setEditableDetail(normalizeTopicDetail(topicDetail));
            setIsEditing(false);
            setValidationErrors({});
        }
    }, [topicDetail, isCreating]);

    const createEmptyTopic = (): TopicDetailDto => ({
        id: 0,
        parentId: undefined,
        parentDescription: {
            catalan: "",
            spanish: "",
            english: "",
        },
        code: "",
        description: {
            catalan: "",
            spanish: "",
            english: "",
        },
        promptExample: "",
        responseModel: "",
    });

    const handleStartCreate = () => {
        setErrorMessage(null);
        setValidationErrors({});
        setPreviousSelectedTopicId(selectedTopicId);
        setSelectedTopicId(null);
        setIsCreating(true);
        setIsEditing(true);
        setEditableDetail(createEmptyTopic());
    };

    const handleCancel = () => {
        setErrorMessage(null);
        setIsSaving(false);
        setValidationErrors({});

        if (isCreating) {
            setIsCreating(false);
            setIsEditing(false);
            const restoredTopicId = previousSelectedTopicId ?? getFirstNodeId(topicTree);
            setSelectedTopicId(restoredTopicId);
            return;
        }

        setEditableDetail(topicDetail ? normalizeTopicDetail(topicDetail) : null);
        setIsEditing(false);
    };

    const handleSave = async () => {
        if (!editableDetail || isSaving) return;

        const schema = getTopicFormSchema(t);
        setValidationErrors({});
        try {
            await schema.validate(editableDetail, { abortEarly: false });
        } catch (err: any) {
            const errors: { [key: string]: string | undefined } = {};
            if (Array.isArray(err?.inner) && err.inner.length > 0) {
                err.inner.forEach((error: any) => {
                    if (error.path) {
                        errors[error.path] = error.message;
                    }
                });
            } else if (err?.path) {
                errors[err.path] = err.message;
            }
            setValidationErrors(errors);
            return;
        }

        setErrorMessage(null);
        setIsSaving(true);

        try {
            const savedTopic = isCreating
                ? await createTopic(editableDetail)
                : await updateTopic(editableDetail.id, editableDetail);

            setEditableDetail(normalizeTopicDetail(savedTopic));
            setIsEditing(false);
            setIsCreating(false);
            showSuccessMessage(isCreating ? t("topic:form.createSuccessfully") : t("topic:form.updateSuccessfully"), 2000);

            await queryClient.invalidateQueries({ queryKey: ["adminTopicsTree"] });

            if (savedTopic?.id) {
                await queryClient.invalidateQueries({ queryKey: ["adminTopicDetail", savedTopic.id] });
                setSelectedTopicId(savedTopic.id);
            } else {
                await queryClient.invalidateQueries({ queryKey: ["adminTopicDetail"] });
            }
        } catch (error) {
            const apiError = error as ApiErrorResponse;
            setErrorMessage(apiError?.message ?? t("common:requestError.errorBadRequest"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = () => {
        const hasActiveSubtopics: boolean = getAllNodes(topicTree).find(topic => topic.id == selectedTopicId)!.childrens.length > 0;
        if (hasActiveSubtopics) {
            openCannotDeleteModal(
                null,
                "",
                t("topic:hasActiveSubtopicsError"),
                "info",
                ""
            );
            return;
        }
        openDeleteModal(
            null,
            t("topic:modal.deleteTitle"),
            t("topic:modal.deleteText", { name: (editableDetail ? getTranslatedDescription(editableDetail?.description, i18n.language) : "") }),
            "deleteWithoutInput",
            ""
        );
    }

    const confirmDelete = () => {
        setDeleteLoading(true);
        deleteTopic(selectedTopicId!)
            .then(async () => {
                showSuccessMessage(t("topic:form.deleteSuccessfully"), 2000);
                setSelectedTopicId(previousSelectedTopicId);
                await queryClient.invalidateQueries({ queryKey: ["adminTopicsTree"] });
            })
            .catch((error) => {
                setErrorMessage((error as ApiErrorResponse)?.message ?? t("common:requestError.errorBadRequest"));
            }).finally(() => {
                setDeleteLoading(false);
                closeDeleteModal();
            });
    }

    const confirmExportToCSV = () => {
        setExportLoading(true);
        exportTopicsToCsv(searchText)
            .then(() => {
            })
            .catch((error) => {
                setErrorMessage((error as ApiErrorResponse)?.message ?? t("common:requestError.errorBadRequest"));
            }).finally(() => {
                setExportLoading(false);
                closeExportModal();
            });
    }

    const showSuccessMessage = (message: string, displayTime: number) => {
        setErrorMessage(null);
        setSuccessMessage(message);
        
        const timer = setTimeout(() => {
            setSuccessMessage(null);
        }, displayTime);

        return () => clearTimeout(timer);
    }

    return (
        <>
            {
                !showImportFromCsvPage ?
                <>
                    <ModalGlobal
                        show={showCannotDeleteModal}
                        onClose={closeCannotDeleteModal}
                        title={cannotDeleteModalData.title}
                        contentType={cannotDeleteModalData.type}
                        subtitle={cannotDeleteModalData.subtitle}
                        closeText={t("common:buttons.buttonClose")}
                        isLoading={false}
                    />
                    <ModalGlobal
                        show={showDeleteModal}
                        onClose={closeDeleteModal}
                        title={deleteModalData.title}
                        contentType={deleteModalData.type}
                        subtitle={deleteModalData.subtitle}
                        saveText={t("common:buttons.buttonAccept")}
                        closeText={t("common:buttons.buttonCancel")}
                        onSave={() => confirmDelete()}
                        isLoading={isDeleteLoading}
                    />
                    <ModalGlobal
                        show={showExportModal}
                        onClose={closeExportModal}
                        title={exportModalData.title}
                        contentType={exportModalData.type}
                        subtitle={exportModalData.subtitle}
                        saveText={t("common:modalActionOK")}
                        closeText={t("common:modalActionNOTOK")}
                        onSave={confirmExportToCSV}
                        isLoading={isExportLoading}
                    />
                    <Row className="mb-3 align-items-center">
                        <Col xs={12} md={8}>
                            <h2>{t("topic:managementTitle")}</h2>
                        </Col>
                        <Col xs={12} md={4} className="text-end">
                            {permissions.length > 0 && permissions.includes(PERMISSIONS.TOPIC_MANAGEMENT.CREATE) && (
                                <Button
                                    variant="primary"
                                    onClick={handleStartCreate}
                                    disabled={isEditing || isSaving}
                                >
                                    {t("topic:newTopic")}
                                </Button>
                            )}
                        </Col>
                    </Row>

                    {(treeError || detailError || errorMessage) && (
                        <Row>
                            <Col xs={12}>
                                <AlertMessage
                                    message={
                                        (treeError as ApiErrorResponse)?.message ||
                                        (detailError as ApiErrorResponse)?.message ||
                                        errorMessage ||
                                        t("common:requestError.errorBadRequest")
                                    }
                                    type="danger"
                                />
                            </Col>
                        </Row>
                    )}
                    {(successMessage) && (
                        <Row>
                            <Col xs={12}>
                                <AlertMessage
                                    message={successMessage}
                                    type="success"
                                />
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col xs={12} md={6}>
                            <TopicsTreePanel
                                isLoading={isLoadingTree}
                                nodes={topicTree}
                                selectedTopicId={selectedTopicId}
                                onSelectTopic={setSelectedTopicId}
                                getNodeName={(node) => getTranslatedDescription(node.name, i18n.language)}
                                searchText={searchText}
                                setSearchText={setSearchText}
                            />
                            <Row className="mt-2 d-flex">
                                <Col xs={6}>
                                    {permissions.length > 0 && permissions.includes(PERMISSIONS.TOPIC_MANAGEMENT.IMPORT) && (
                                        <a style={{textDecoration: "none"}}
                                            onClick={downloadTopicsTemplate}
                                        >
                                            {t("topic:downloadTemplate")}
                                        </a>
                                    )}
                                </Col>
                                <Col xs={6} className="text-end">
                                    {permissions.length > 0 && permissions.includes(PERMISSIONS.TOPIC_MANAGEMENT.IMPORT) && (
                                        <a className="me-4"
                                            style={{textDecoration: "none"}}
                                            onClick={() => setShowImportFromCsvPage(true)}
                                        >
                                            {t("topic:linkImportCSV")}
                                        </a>
                                    )}
                                    {permissions.length > 0 && permissions.includes(PERMISSIONS.TOPIC_MANAGEMENT.EXPORT) && (
                                        <a  onClick={() => openExportModal(
                                                null,
                                                "",
                                                t("topic:modal.exportCsvText"),
                                                "exportCsv",
                                                "")
                                            }
                                            style={{textDecoration: "none"}}
                                        >
                                            {t("common:pagination.linkExportCSV")}
                                        </a>
                                    )}
                                </Col>                                
                            </Row>
                        </Col>

                        <Col xs={12} md={6}>
                            <TopicDetailForm
                                isLoading={selectedTopicId != null ? isLoadingDetail : false}
                                editableDetail={editableDetail}
                                isEditing={isEditing}
                                isCreating={isCreating}
                                isSaving={isSaving}
                                errors={validationErrors}
                                setIsEditing={setIsEditing}
                                setEditableDetail={setEditableDetail}
                                setValidationErrors={setValidationErrors}
                                topicOptions={topicOptions}
                                onCancel={handleCancel}
                                onSave={handleSave}
                                onDelete={handleDelete}
                            />
                        </Col>
                    </Row>
                </> :
                <TopicImportFromCSV setShowImportFromCsvPage={setShowImportFromCsvPage}/>
            }

        </>
    );
}
